import React, { useState, useEffect } from 'react';

import { SPARK_URL } from './apiConfig';

// Simple Loader component (no lucide-react needed)
const Loader = () => (
  <div style={{
    width: '48px',
    height: '48px',
    border: '4px solid rgba(255, 255, 255, 0.3)',
    borderTopColor: 'white',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite'
  }} />
);

// Simple icons (no lucide-react needed)
const ShieldIcon = () => (
  <div style={{ fontSize: '40px' }}>🛡️</div>
);

const CheckIcon = () => (
  <span style={{ fontSize: '20px' }}>✅</span>
);

const AlertIcon = () => (
  <span style={{ fontSize: '20px' }}>⚠️</span>
);

export default function VerificationScreen({ onVerified, onSkip }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    checkVerificationStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkVerificationStatus = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${SPARK_URL}/verification/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStatus(data.data);
        
        // If already verified, call onVerified callback
        if (data.data?.isVerified) {
          onVerified && onVerified();
        }
      } else {
        // If endpoint doesn't exist or fails, just continue
        console.warn('Verification status endpoint not available, continuing...');
      }
    } catch (err) {
      console.error('Failed to check verification status:', err);
      // Don't set error - just continue silently
    } finally {
      setLoading(false);
    }
  };

  const handleStartVerification = async () => {
    setVerifying(true);
    setError('');
    
    try {
      const token = localStorage.getItem('accessToken');
      
      // DEVELOPMENT: Use simulate endpoint
      const response = await fetch(`${SPARK_URL}/verification/simulate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ approved: true })
      });

      const data = await response.json();

      if (data.success) {
        alert('✅ Identity verified! Welcome to Spark!');
        await checkVerificationStatus();
        onVerified && onVerified();
      } else {
        setError(data.message || 'Verification failed. Please try again.');
      }
    } catch (err) {
      console.error('Verification error:', err);
      setError('An error occurred during verification. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        minHeight: '100dvh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        zIndex: 9999
      }}>
        <Loader />
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      minHeight: '100dvh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      zIndex: 9999,
      overflowY: 'auto'
    }}>
      <div style={{
        maxWidth: '500px',
        width: '100%',
        background: 'white',
        borderRadius: '24px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
        padding: '40px',
        animation: 'slideUp 0.3s ease-out'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '24px'
          }}>
            <ShieldIcon />
          </div>
          
          <h1 style={{
            fontSize: '28px',
            fontWeight: 700,
            color: '#2c3e50',
            marginBottom: '12px',
            margin: 0
          }}>
            Verify Your Identity
          </h1>
          
          <p style={{
            fontSize: '16px',
            color: '#7f8c8d',
            lineHeight: '1.6',
            marginTop: '12px'
          }}>
            To keep Spark safe for everyone, we require identity verification before you can start matching.
          </p>
        </div>

        {/* What You'll Need */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '24px'
        }}>
          <h3 style={{
            fontSize: '16px',
            fontWeight: 600,
            color: '#2c3e50',
            marginBottom: '16px',
            marginTop: 0
          }}>
            What you'll need:
          </h3>
          
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <div style={{ marginRight: '12px', flexShrink: 0, marginTop: '2px' }}>
                <CheckIcon />
              </div>
              <span style={{ fontSize: '14px', color: '#2c3e50', lineHeight: '1.5' }}>
                Government-issued ID (passport, driver's license, or ID card)
              </span>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <div style={{ marginRight: '12px', flexShrink: 0, marginTop: '2px' }}>
                <CheckIcon />
              </div>
              <span style={{ fontSize: '14px', color: '#2c3e50', lineHeight: '1.5' }}>
                2-3 minutes of your time
              </span>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'flex-start' }}>
              <div style={{ marginRight: '12px', flexShrink: 0, marginTop: '2px' }}>
                <CheckIcon />
              </div>
              <span style={{ fontSize: '14px', color: '#2c3e50', lineHeight: '1.5' }}>
                A selfie for face matching
              </span>
            </div>
          </div>
        </div>

        {/* Privacy Notice */}
        <div style={{
          background: '#fff9e6',
          border: '1px solid #ffd700',
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'flex-start'
        }}>
          <div style={{ marginRight: '12px', flexShrink: 0, marginTop: '2px' }}>
            <AlertIcon />
          </div>
          <div style={{ fontSize: '14px', color: '#7f8c8d', lineHeight: '1.6' }}>
            <strong style={{ color: '#2c3e50' }}>Privacy guaranteed:</strong> Your ID is verified securely and all sensitive data is automatically deleted after 30 days.
          </div>
        </div>

        {/* Status Display */}
        {status && !status.isVerified && (
          <div style={{
            background: '#fff3e0',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span style={{ fontSize: '14px', color: '#7f8c8d' }}>
              Verification Status:
            </span>
            <span style={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#f57c00',
              textTransform: 'capitalize'
            }}>
              {status.status === 'not_started' ? 'Not Started' : status.status}
            </span>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div style={{
            background: '#ffebee',
            border: '1px solid #ef5350',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '24px',
            color: '#c62828',
            fontSize: '14px'
          }}>
            {error}
          </div>
        )}

        {/* Verification Button */}
        <button
          onClick={handleStartVerification}
          disabled={verifying}
          style={{
            width: '100%',
            padding: '16px',
            background: verifying 
              ? '#95a5a6' 
              : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none',
            borderRadius: '12px',
            fontSize: '16px',
            fontWeight: 600,
            cursor: verifying ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            transition: 'all 0.3s ease',
            marginBottom: '16px'
          }}
        >
          {verifying ? (
            <>
              <div style={{
                width: '20px',
                height: '20px',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                borderTopColor: 'white',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
              Verifying...
            </>
          ) : (
            <>
              🛡️ Start Verification
            </>
          )}
        </button>

        {/* Skip Button */}
        {onSkip && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onSkip();
            }}
            style={{
              width: '100%',
              padding: '12px',
              background: 'transparent',
              color: '#7f8c8d',
              border: 'none',
              fontSize: '14px',
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
          >
            I'll do this later
          </button>
        )}

        {/* Terms Text */}
        <p style={{
          fontSize: '12px',
          color: '#95a5a6',
          textAlign: 'center',
          marginTop: '24px',
          lineHeight: '1.5',
          margin: '24px 0 0 0'
        }}>
          By verifying, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
